import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { uploadFile, deleteFile, generateFilePath } from './storage';

// Get jeweller profile - INCLUDING brandIcon
export const getJewellerProfile = async (jewellerId) => {
  try {
    const jewellerDocRef = doc(db, 'jewellers', jewellerId);
    const jewellerDoc = await getDoc(jewellerDocRef);
    
    if (jewellerDoc.exists()) {
      const data = jewellerDoc.data();
      return { 
        id: jewellerDoc.id, 
        ...data,
        // Ensure brandIcon is included (default to empty string if not present)
        brandIcon: data.brandIcon || ''
      };
    } else {
      throw new Error('Jeweller profile not found');
    }
  } catch (error) {
    console.error('Error fetching jeweller profile:', error);
    throw error;
  }
};

// Update jeweller profile - NOW HANDLES brandIcon
export const updateJewellerProfile = async (jewellerId, profileData) => {
  try {
    console.log("Updating jeweller profile with:", profileData);
    const jewellerDocRef = doc(db, 'jewellers', jewellerId);
    
    // Special handling for ethical statement if it exists
    if (profileData.tags) {
      const statement = (profileData.ethicalStatement || '').trim();

      // keep a copy in the tags object if downstream consumers expect it there
      profileData.tags.ethicalStatement = statement;

      if (statement.length < 100) {
        profileData.tags.ethicalJeweller = false;
      } else {
        profileData.tags.ethicalJeweller = true;
      }
    }
    
    const updateData = {};
    const directProfileFields = [
      'companyName',
      'location',
      'email',
      'website',
      'instagram',
      'introParagraph',
      'startingPrice',
      'minimumTimeframe',
      'headerImage',
      'firstName',
      'surname',
      'profilePhoto',
      'brandIcon',
      'title',
      'bio',
      'videoUrl',
      'showcaseVideo',
      'galleryPhotos',
      'galleryImages',
      'awards',
      'tags',
      'ethicalStatement',
      'completionPercentage'
    ];

    directProfileFields.forEach((field) => {
      if (field in profileData) {
        updateData[field] = profileData[field];
      }
    });

    // If there are additional fields in profileData, include them
    Object.keys(profileData).forEach(key => {
      if (!(key in updateData) && key !== 'id') {
        updateData[key] = profileData[key];
      }
    });

    updateData.updatedAt = new Date();
    
    // Update profile
    await updateDoc(jewellerDocRef, updateData);
    
    console.log('Profile updated successfully, including brandIcon:', updateData.brandIcon ? 'present' : 'empty');
    return true;
  } catch (error) {
    console.error('Error updating jeweller profile:', error);
    throw error;
  }
};

// Upload header image
export const uploadHeaderImage = async (jewellerId, imageFile) => {
  try {
    // Generate file path
    const path = generateFilePath(jewellerId, 'header', imageFile.name);
    
    // Upload file
    const imageUrl = await uploadFile(path, imageFile);
    
    // Update jeweller document with new image URL
    const jewellerDocRef = doc(db, 'jewellers', jewellerId);
    await updateDoc(jewellerDocRef, {
      headerImage: imageUrl,
      updatedAt: new Date()
    });
    
    return imageUrl;
  } catch (error) {
    console.error('Error uploading header image:', error);
    throw error;
  }
};

// Upload key person image
export const uploadKeyPersonImage = async (jewellerId, imageFile) => {
  try {
    // Generate file path
    const path = generateFilePath(jewellerId, 'profile', imageFile.name);
    
    // Upload file
    const imageUrl = await uploadFile(path, imageFile);
    
    // Get current jeweller data
    const jewellerDocRef = doc(db, 'jewellers', jewellerId);
    const jewellerDoc = await getDoc(jewellerDocRef);
    
    if (!jewellerDoc.exists()) {
      throw new Error('Jeweller not found');
    }
    
    const currentData = jewellerDoc.data();
    
    // Build update data based on existing data structure
    let updateData = {};
    
    if (currentData.keyPerson) {
      // If keyPerson exists, just update the photo field
      updateData = {
        'keyPerson.photo': imageUrl,
        updatedAt: new Date()
      };
    } else {
      // If keyPerson doesn't exist, create an object with photo
      updateData = {
        keyPerson: {
          photo: imageUrl,
          name: '',
          bio: '',
          title: '',
          postcode: ''
        },
        updatedAt: new Date()
      };
    }
    
    // Update document
    await updateDoc(jewellerDocRef, updateData);
    
    return imageUrl;
  } catch (error) {
    console.error('Error uploading key person image:', error);
    throw error;
  }
};

// Upload gallery photo
export const uploadGalleryPhoto = async (jewellerId, imageFile) => {
  try {
    // Generate file path
    const path = generateFilePath(jewellerId, 'gallery', imageFile.name);
    
    // Upload file
    const imageUrl = await uploadFile(path, imageFile);
    
    // Get current gallery photos
    const jewellerDocRef = doc(db, 'jewellers', jewellerId);
    const jewellerDoc = await getDoc(jewellerDocRef);
    const currentGalleryPhotos = jewellerDoc.data()?.galleryPhotos || {};
    
    // Add new photo to gallery
    const photoId = `photo_${Date.now()}`;
    const updatedGalleryPhotos = {
      ...currentGalleryPhotos,
      [photoId]: imageUrl
    };
    
    // Update profile with new gallery photos
    await updateDoc(jewellerDocRef, {
      galleryPhotos: updatedGalleryPhotos,
      updatedAt: new Date()
    });
    
    return { id: photoId, url: imageUrl };
  } catch (error) {
    console.error('Error uploading gallery photo:', error);
    throw error;
  }
};

// Remove gallery photo
export const removeGalleryPhoto = async (jewellerId, photoId) => {
  console.log(`Removing photo ${photoId} for jeweller ${jewellerId}`);
  try {
    // Get current gallery photos
    const jewellerDocRef = doc(db, 'jewellers', jewellerId);
    const jewellerDoc = await getDoc(jewellerDocRef);
    const currentGalleryPhotos = jewellerDoc.data()?.galleryPhotos || {};
    
    // Check if photo exists
    if (!currentGalleryPhotos[photoId]) {
      console.warn('Photo not found:', photoId);
      return false;
    }
    
    // Get the URL to delete from storage
    const photoUrl = currentGalleryPhotos[photoId];
    console.log('Photo URL to delete:', photoUrl);
    
    // Delete the file from storage if possible
    try {
      if (photoUrl && photoUrl.includes('firebasestorage')) {
        await deleteFile(photoUrl);
        console.log('Deleted file from storage');
      }
    } catch (deleteError) {
      console.error('Error deleting gallery photo from storage:', deleteError);
      // Continue with database removal even if storage delete fails
    }
    
    // Remove the photo from gallery photos object
    const updatedGalleryPhotos = { ...currentGalleryPhotos };
    delete updatedGalleryPhotos[photoId];
    
    // Update document
    await updateDoc(jewellerDocRef, {
      galleryPhotos: updatedGalleryPhotos,
      updatedAt: new Date()
    });
    
    console.log('Gallery photo removed from database');
    return true;
  } catch (error) {
    console.error('Error removing gallery photo:', error);
    throw error;
  }
};

// Generic image upload (used by ImageUpload component)
// This function works for both profilePhoto and brandIcon uploads
export const uploadImage = async (file) => {
  const timestamp = Date.now();
  const fileName = `${timestamp}_${file.name}`;
  const path = `jewellers/uploads/${fileName}`;
  
  // Use your existing uploadFile from storage.js
  const imageUrl = await uploadFile(path, file);
  return imageUrl;
};

// Helper function to check if a jeweller has brandIcon (for migration)
export const checkBrandIconExists = async (jewellerId) => {
  try {
    const jewellerProfile = await getJewellerProfile(jewellerId);
    return !!jewellerProfile.brandIcon;
  } catch (error) {
    console.error('Error checking brandIcon:', error);
    return false;
  }
};

// Migration helper - add brandIcon field to existing profiles
export const migrateBrandIconField = async (jewellerId) => {
  try {
    const hasIcon = await checkBrandIconExists(jewellerId);
    
    if (!hasIcon) {
      const jewellerDocRef = doc(db, 'jewellers', jewellerId);
      await updateDoc(jewellerDocRef, {
        brandIcon: '',
        updatedAt: new Date()
      });
      console.log('Added brandIcon field to profile:', jewellerId);
      return true;
    }
    
    console.log('Profile already has brandIcon field:', jewellerId);
    return false;
  } catch (error) {
    console.error('Error migrating brandIcon field:', error);
    throw error;
  }
};
